/**
 * ConfirmPickupPage — driver picks up cargo from sender. Mirror of
 * `delivery/pickup_confirmation_page.dart`.
 *
 * Same shape as ConfirmDelivery: OTP from sender + photo + optional
 * signature. POST /api/cargo/shipments/:id/pickup-otp triggers the
 * sender's OTP, then /confirm-pickup finalises the handover.
 */
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Package, MessageCircle, Camera, AlertCircle, Loader, Hash,
} from 'lucide-react';
import { useShipment, useGeneratePickupOtp, useConfirmPickup } from '@/hooks/useShipments';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import { TextField } from '@/components/common/TextField';

export default function ConfirmPickupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const idNum = id ? parseInt(id, 10) : null;
  const { data: shipment, isLoading } = useShipment(idNum);
  const sendOtp = useGeneratePickupOtp();
  const confirm = useConfirmPickup();

  const [otpCode, setOtpCode] = useState('');
  const [photoUrl, setPhotoUrl] = useState('');
  const [pieceCount, setPieceCount] = useState('1');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  if (isLoading || !idNum) {
    return (
      <AppLayout>
        <div className="flex justify-center py-16 text-text-tertiary">
          <Loader size={32} className="animate-spin" />
        </div>
      </AppLayout>
    );
  }
  if (!shipment) {
    return <AppLayout><div className="bg-error/10 text-error rounded-xl p-4">الشحنة غير موجودة</div></AppLayout>;
  }

  async function handleSendOtp() {
    setError(null);
    try {
      await sendOtp.mutateAsync(idNum!);
      setOtpSent(true);
    } catch (e: any) {
      setError(e?.message || 'فشل إرسال الكود');
    }
  }

  async function handleConfirm() {
    setError(null);
    if (!otpCode.trim() && !photoUrl.trim()) {
      setError(t('pickup_proof_required', 'يجب إدخال OTP أو رابط صورة على الأقل'));
      return;
    }
    try {
      const pieces = parseInt(pieceCount, 10);
      await confirm.mutateAsync({
        id: idNum!,
        otpCode: otpCode.trim() || undefined,
        photoUrl: photoUrl.trim() || undefined,
        pieceCount: Number.isFinite(pieces) ? pieces : undefined,
      });
      setSuccess(true);
      setTimeout(() => navigate(`/shipments/${idNum}`, { replace: true }), 1500);
    } catch (e: any) {
      setError(e?.message || 'فشل تأكيد الاستلام');
    }
  }

  if (success) {
    return (
      <AppLayout>
        <div className="bg-success/10 border border-success/30 rounded-2xl p-8 text-center max-w-md mx-auto">
          <Package size={56} className="mx-auto text-success mb-3" />
          <h2 className="text-xl font-bold text-success">{t('pickup_confirmed', 'تم استلام الشحنة')}</h2>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary">
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="bg-surface border border-border rounded-2xl p-6 max-w-xl mx-auto">
        <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
          <Package size={20} /> {t('confirm_pickup', 'تأكيد الاستلام من المرسِل')}
        </h1>
        <div className="flex items-center gap-3 text-text-secondary text-sm mb-5">
          <span className="font-mono flex items-center gap-1"><Hash size={12} /> {shipment.tracking_number}</span>
          <span>· {shipment.sender_name || t('sender', 'المرسِل')}</span>
        </div>

        {error && (
          <div className="bg-error/10 text-error rounded-xl p-3 text-sm mb-4 flex items-center gap-2">
            <AlertCircle size={14} /> {error}
          </div>
        )}

        <p className="text-text-secondary text-sm mb-5">
          {t('confirm_pickup_intro',
             'تأكّد من أنّ الكرتون مطابق للوصف والوزن. أدخل OTP المرسِل وارفع صورة بالاستلام.')}
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              1. {t('pickup_otp', 'كود OTP من المرسِل')}
            </label>
            <div className="flex items-end gap-2 mb-2">
              <Button variant="ghost" onClick={handleSendOtp} loading={sendOtp.isPending}>
                <MessageCircle size={14} /> {otpSent ? t('resend_otp', 'إعادة إرسال') : t('send_otp_to_sender', 'إرسال الكود للمرسِل')}
              </Button>
              {otpSent && <span className="text-xs text-success">✓ أُرسِل</span>}
            </div>
            <TextField
              dir="ltr"
              placeholder="123456"
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="font-mono text-lg tracking-widest text-center"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              2. {t('pieces', 'عدد القطع')}
            </label>
            <TextField
              type="number"
              dir="ltr"
              value={pieceCount}
              onChange={(e) => setPieceCount(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2 flex items-center gap-1">
              <Camera size={14} /> 3. {t('pickup_photo_url', 'رابط صورة الاستلام (اختياري)')}
            </label>
            <TextField
              dir="ltr"
              placeholder="https://…"
              value={photoUrl}
              onChange={(e) => setPhotoUrl(e.target.value)}
            />
          </div>

          <Button onClick={handleConfirm} loading={confirm.isPending} fullWidth>
            <Package size={16} /> {t('finalize_pickup', 'تأكيد الاستلام')}
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
